import React, { useCallback, useEffect, useRef, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import { phoneUi } from "../../ui/phoneUi";
import RoutingStrip from "../../../components/RoutingStrip.jsx";
import { CoatingSwatch } from "../../../components/CoatingColor";
import { loadCutSheet, loadJobDefects, loadJobDetail, loadJobParts, rowStatus } from "../../../features/queue/queueData";
import { taskButtons, taskGates } from "../../../features/queue/queueRules";
import {
  autoCompleteTask, completeTask, enterMaintenance, exitMaintenance, finishOp, pauseTask, recordPartDefect, startTask,
} from "../../../features/queue/taskActions";
import { taskRoutingSteps } from "../routing";
import { formatDueDate } from "../../../utils/dueDate";
import { fmtSeconds, intervalToSeconds } from "../../../utils/multiOpFlow";
import { queueRowSaysTm } from "../../../utils/tmDefects";
import { StatusPill, TaskChips, TimeLeft } from "./QueueTaskCard.jsx";
import TravelerButton from "./TravelerButton.jsx";
import TaskPalletsButton from "../../../components/TaskPalletsButton.jsx";
import TaskPalletsPanel from "../../../components/TaskPalletsPanel.jsx";

// One task, full screen: what the desktop's detail view shows that an operator needs on the floor
// (routing, parts, notes, defects) and the task's actions at the bottom. Layout only: the reads
// come from features/queue/queueData, the buttons from queueRules.taskButtons, and every action is
// the desktop's own (features/queue/taskActions) with the phone's dialogs (mobile/ui/phoneUi).
// The Laser is monitor-only on a phone: Pause / Resume, nothing else.

const TONE = {
  success: "btn-success",
  warning: "btn-warning",
  danger: "btn-danger",
  primary: "btn-primary",
  info: "btn-info",
  secondary: "btn-secondary",
};

/** The parts this task works on: its own part, or every part of its cut list. */
function partsForTask(task, row, jobParts) {
  if (task.part_id != null) {
    const p = (jobParts || []).find((jp) => jp.part_id === task.part_id);
    return p ? [p] : [];
  }
  if (Array.isArray(task.parts) && task.parts.length) return task.parts;
  return Array.isArray(row.parts) ? row.parts : [];
}

export default function TaskSheet({ row, station, stationLocked, nextAllowed, pendingMaintenanceToday, onClose, onChanged }) {
  const [data, setData] = useState({ loading: true, error: null, detail: null, allTasks: [], parts: [], defects: [], cutSheetParts: null });
  const [busy, setBusy] = useState(false);
  const [travelerUrl, setTravelerUrl] = useState(null); // set by a Complete (the phone prints nothing)
  const runningRef = useRef(false);
  const loadSeq = useRef(0);
  const isLaser = station === "Laser";

  // The job and its progress decide the buttons, so they show as soon as those two answer; parts,
  // defects (and the Laser's cut sheet, for the PPAP reminder on Resume) fill in after. A failed
  // re-read keeps what was shown but marks it stale: the buttons are disabled until Retry works.
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const fresh = () => seq === loadSeq.current;
    let detail;
    let allTasks;
    try {
      ({ detail, allTasks } = await loadJobDetail({
        jobId: row.job_id, taskId: row.task_id, workstation: station, preShippedQty: row.pre_shipped_qty,
      }));
    } catch (err) {
      if (fresh()) setData((d) => ({ ...d, loading: false, error: err.message || "Couldn't load this task." }));
      return;
    }
    if (!fresh()) return;
    setData((d) => ({ ...d, loading: false, error: null, detail, allTasks }));
    loadJobParts(row.job_id)
      .then((parts) => { if (fresh() && parts) setData((d) => ({ ...d, parts })); })
      .catch((err) => console.warn("Job parts did not load:", err));
    loadJobDefects(row.job_id).then((defects) => { if (fresh()) setData((d) => ({ ...d, defects })); });
    const own = (detail.tasks || []).find((t) => t.task_id === row.task_id);
    if (isLaser && own && own.cut_sheet_id) {
      loadCutSheet(own.cut_sheet_id)
        .then((cs) => { if (fresh() && cs) setData((d) => ({ ...d, cutSheetParts: cs.parts || null })); })
        .catch((err) => console.warn("Cut sheet did not load:", err));
    }
  }, [row.job_id, row.task_id, row.pre_shipped_qty, station, isLaser]);

  useEffect(() => {
    load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  const { detail, allTasks, parts: jobParts, defects, cutSheetParts } = data;
  const task = detail ? (detail.tasks || []).find((t) => t.task_id === row.task_id) : null;
  const status = task ? task.status : rowStatus(row);

  // One action at a time (a second tap while a PIN sheet is up does nothing), then re-read.
  const run = async (action) => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const result = await action();
      if (result && result.travelerUrl) setTravelerUrl(result.travelerUrl);
      if (result && result.reload) {
        await load();
        onChanged();
      }
    } finally {
      runningRef.current = false;
    }
  };

  let gates = null;
  let buttons = [];
  let notes = [];
  if (task) {
    gates = taskGates({
      job: detail, task, workstation: station, selectedTaskId: row.task_id, stationLocked, nextAllowed, pendingMaintenanceToday,
    });
    if (isLaser) {
      // Monitor only: the cut is started and completed at the laser (Add Sheet, FAI, labels).
      if (status === "In Progress") buttons = [{ action: "pause", label: "Pause", tone: "warning" }];
      else if (status === "Paused" && !gates.controlsDisabled) buttons = [{ action: "start", label: "Resume", tone: "success" }];
      notes = ["The Laser is started and completed at the station. From a phone: Pause and Resume."];
    } else {
      ({ buttons, notes } = taskButtons({ task, workstation: station, gates }));
    }
  }

  const ACTIONS = gates && {
    start: () => startTask({ task, workstation: station, isMaintenanceTask: gates.isMaintenanceTask, cutSheetParts, ui: phoneUi, setBusy }),
    pause: () => pauseTask({ task, ui: phoneUi, setBusy }),
    finishOp: () => finishOp({ task, opFlow: gates.opFlow, ui: phoneUi, setBusy }),
    complete: () => completeTask({ task, workstation: station, isMaintenanceTask: gates.isMaintenanceTask, jobId: detail.job_id, ui: phoneUi, setBusy }),
    autoComplete: () => autoCompleteTask({ task, jobId: detail.job_id, isMaintenanceTask: gates.isMaintenanceTask, ui: phoneUi, setBusy }),
    enterMaintenance: () => enterMaintenance({ task, ui: phoneUi, setBusy }),
    exitMaintenance: () => exitMaintenance({ task, ui: phoneUi, setBusy }),
  };

  const partsHere = task ? partsForTask(task, row, jobParts) : [];
  const flagged = (jobParts || []).filter((p) => p.flagged);
  const estimated = row.estimated_time || (task && task.estimated_time);
  const est = intervalToSeconds(estimated);
  const rowState = rowStatus(row);
  const due = row.task_due_date ?? row.due_date ?? (detail && detail.due_date);
  const steps = task ? taskRoutingSteps(allTasks, detail.decoupled_defects, task) : [];
  const showDefect = Boolean(task) && !isLaser && status !== "Completed";

  return (
    <FullScreenPanel
      open
      title={`Task #${row.task_id}`}
      onClose={onClose}
      testId="queue-task-sheet"
      footer={
        travelerUrl ? (
          <TravelerButton url={travelerUrl} taskId={row.task_id} />
        ) : buttons.length > 0 ? (
          <div className="jq-actions" data-testid="queue-task-actions">
            {buttons.map((b) => (
              <button key={b.action} type="button" className={`btn btn-lg ${TONE[b.tone] || "btn-secondary"}`} disabled={busy || Boolean(data.error) || Boolean(b.disabled)} onClick={() => run(ACTIONS[b.action])}>
                {b.label}
              </button>
            ))}
          </div>
        ) : null
      }
    >
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">Job #{row.master_job_num}</span>
        <StatusPill status={status} />
      </div>
      <div className="jq-qcard-title">{row.product_description || row.job_description || row.maintenance_title}</div>
      <TaskChips row={{ ...row, ...(task || {}), task_status: status }} />

      <dl className="jq-kv">
        {row.customer_name && <div><dt>Customer</dt><dd>{row.customer_name}</dd></div>}
        {due && <div><dt>Due</dt><dd>{formatDueDate(due)}</dd></div>}
        {(row.remaining ?? row.quantity) != null && <div><dt>Qty</dt><dd>{row.remaining ?? row.quantity}</dd></div>}
        {est > 0 && <div><dt>Estimated</dt><dd>{fmtSeconds(est)}</dd></div>}
        {/* The queue row's own status + timing (the screen re-reads the row after every action). */}
        {(rowState === "In Progress" || rowState === "Paused") && est > 0 && (
          <div><dt>Time left</dt><dd><TimeLeft row={row} status={rowState} estimated={estimated} /></dd></div>
        )}
        {row.sales_order_number && <div><dt>Order</dt><dd>{row.sales_order_number}</dd></div>}
      </dl>

      {data.loading && (
        <div className="jq-list-state" role="status">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" /> Loading the job…
        </div>
      )}
      {data.error && (
        <div className="jq-note jq-note-danger" role="alert">
          {data.error}
          {detail ? "\nWhat is shown may be out of date." : ""}
          <div className="mt-2">
            <button type="button" className="btn btn-outline-danger" onClick={() => { setData((d) => ({ ...d, loading: true })); load(); }}>
              Try again
            </button>
          </div>
        </div>
      )}

      {notes.map((n) => <div key={n} className="jq-note jq-note-info">{n}</div>)}
      {isLaser && task && <TaskPalletsButton taskId={row.task_id} status={status} />}
      {!isLaser && task && <TaskPalletsPanel taskId={row.task_id} workstation={station} />}
      {gates && gates.showMaintenanceNudge && (
        <div className="jq-note">Maintenance due today: {gates.maintenanceTitles}. Starts are blocked from 2 PM.</div>
      )}
      {task && task.remake_hold && task.remake_hold.message && <div className="jq-note jq-note-danger">{task.remake_hold.message}</div>}
      {status === "Maintenance" && (row.maintenance_reason || (task && task.maintenance_reason)) && (
        <div className="jq-note">🔧 {row.maintenance_reason || task.maintenance_reason}</div>
      )}
      {detail && detail.notes && <div className="jq-note"><strong>Job note</strong>{"\n"}{detail.notes}</div>}
      {flagged.length > 0 && (
        <div className="jq-note jq-note-danger">
          <strong>Flagged part{flagged.length > 1 ? "s" : ""}</strong>
          {flagged.map((p) => `\n${p.product_id}: ${p.flag_reason || "flagged"}${p.flagged_by_name ? ` (${p.flagged_by_name})` : ""}`)}
        </div>
      )}

      {steps.length > 0 && (
        <section className="jq-block" aria-label="Routing">
          <h3>Routing</h3>
          <RoutingStrip steps={steps} ariaLabel={`Routing for task ${row.task_id}`} />
        </section>
      )}

      {task && (
        <section className="jq-block" aria-label="Parts">
          <h3>{partsHere.length > 1 ? `Parts (${partsHere.length})` : "Part"}</h3>
          {partsHere.length === 0 && !data.loading && <div className="jq-muted">No part is linked to this task.</div>}
          {partsHere.map((p) => (
            <div key={p.part_id} className="jq-part" data-testid={`queue-part-${p.part_id}`}>
              {p.image_path ? <img className="jq-part-img" src={p.image_path} alt="" loading="lazy" /> : null}
              <div className="jq-part-body">
                <div className="fw-semibold">{p.product_id || `Part #${p.part_id}`}</div>
                {(p.description || p.part_description) && <div className="jq-muted">{p.description || p.part_description}</div>}
                {station === "Blast" && (p.blast_time_top || p.blast_time_bottom) && (
                  <div className="jq-muted">Blast: top {p.blast_time_top || "-"}s · bottom {p.blast_time_bottom || "-"}s</div>
                )}
                {station === "Coating" && p.coating_color_code && (
                  <div><CoatingSwatch code={p.coating_color_code} name={p.coating_color_name} hex={p.coating_color_hex} size={16} /></div>
                )}
                {p.quantity_per_length && task.quantity ? (
                  <div className="jq-muted">{p.quantity_per_length * task.quantity} pcs ({p.quantity_per_length} per length)</div>
                ) : null}
              </div>
              {showDefect && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={busy || Boolean(data.error)}
                  onClick={() => run(() => recordPartDefect({ job: detail, task, part: p, isTmJob: queueRowSaysTm(row), ui: phoneUi }))}
                >
                  Defect
                </button>
              )}
            </div>
          ))}
        </section>
      )}

      {detail && (
        <section className="jq-block" aria-label="Defects">
          <h3>Defects on this job{defects.length ? ` (${defects.length})` : ""}</h3>
          {defects.length === 0 && <div className="jq-muted">None recorded.</div>}
          {defects.map((d) => (
            <div key={d.defect_id} className="jq-defect-row">
              <strong>{d.defective_quantity}</strong> × {d.product_id || "N/A"} · {d.defect_reason}
              <div className="jq-muted">{d.workstation ? `${d.workstation} · ` : ""}{d.recorded_by_name || ""}</div>
            </div>
          ))}
        </section>
      )}
    </FullScreenPanel>
  );
}
